import { Error, Warning } from './common-types';

/**
 * This is based on the DAPI Swagger version 2.83
 */
export namespace DapiResponseNS {
  export interface DapiAirBoundsResponse {
    data: Data;
    meta: Meta;
    dictionaries: Dictionaries;
    warnings?: Warning[];
    errors?: Error[];
  }

  export interface Data {
    airBoundGroups: AirBoundGroup[];
  }

  export interface AirBoundGroup {
    boundDetails: BoundDetails;
    airBounds: AirBound[];
  }

  export interface AirBound {
    airBoundId: string;
    fareFamilyCode: string;
    isCheapestOffer?: boolean;
    availabilityDetails: AvailabilityDetail[];
    fareInfos: FareInfo[];
    prices: Prices;
    airOffer?: AirOffer;
    fareConditionsCodes: string[];
    status?: AirBoundStatus;
  }

  export interface AirBoundStatus {
    value: AirBoundStatusValue;
    unavailabilityReasonCodes?: AirBoundStatusReason;
  }

  export enum AirBoundStatusValue {
    soldOut = 'soldOut',
    unavailable = 'unavailable',
    waitlist = 'waitlist',
    tooCloseToDeparture = 'tooCloseToDeparture',
    nonCombinableWithSelectedBound = 'nonCombinableWithSelectedBound',
  }

  export enum AirBoundStatusReason {
    noFlights = 'noFlights',
    noFares = 'noFares',
    soldOut = 'soldOut',
    dateCrossover = 'dateCrossover',
    timeCrossover = 'timeCrossover',
  }

  export interface AirOffer {
    totalPrice?: TotalPrice;
    prices?: Prices;
    previousSelectedBound?: PreviousSelectedBound;
  }

  export interface PreviousSelectedBound {
    priceDifference: VariationAmount;
    unitPriceDifferences?: UnitPriceDifference[];
  }

  export interface Prices {
    unitPrices: UnitPrice[];
    totalPrices: TotalPriceElement[];
    isRedemption?: boolean;
    previousSelectionPriceDifference?: PreviousSelectedBound;
  }

  export interface PreviousSelectedBound {
    priceDifference: VariationAmount;
    unitPriceDifferences?: UnitPriceDifference[];
  }

  export interface VariationAmount {
    value: number;
    currencyCode: Currency['name'];
  }

  export interface UnitPriceDifference {
    travelerIds: string[];
    priceDifferences: VariationAmount[];
  }

  export interface TotalPriceElement {
    base: number;
    total: number;
    currencyCode: Currency['name'];
    taxes: Tax[];
    totalTaxes: number;
  }

  export interface Tax {
    value: number;
    currencyCode: Currency['name'];
    code: string;
  }

  export interface UnitPrice {
    travelerIds: string[];
    prices: TotalPriceElement[];
  }

  export interface TotalPrice {
    value: number;
    currencyCode: Currency['name'];
  }

  export interface AvailabilityDetail {
    flightId: string;
    cabin: string;
    bookingClass: string;
    statusCode: string;
    quota: number;
  }

  export interface FareInfo {
    fareType: string;
    fareClass: string;
    pricedPassengerTypeCodes: string[];
    travelerIds: Record<number, string>[];
    flightIds: string[];
    ticketDesignator?: string;
  }

  export interface BoundDetails {
    originLocationCode: string;
    destinationLocationCode: string;
    duration: number;
    ranking: number;
    segments: Segment[];
  }

  export interface Segment {
    flightId: string;
    connectionTime?: number;
  }

  export interface Dictionaries {
    location: Record<string, Location>;
    country: Record<string, string>;
    airline: Record<string, string>;
    aircraft: Record<string, string>;
    flight: Record<string, Flight>;
    currency: Record<string, Currency>;
    anonymousTraveler: Record<string, Traveler>;
    fareFamilyWithServices: Record<string, FareFamilyWithServices>;
    bookingStatus: Record<string, BookingStatus>;
    fareConditions: Record<string, FareConditions>;
  }

  export interface Traveler {
    passengerTypeCode: string;
  }

  export interface BookingStatus {
    name: string;
  }

  export interface Currency {
    name: string;
    decimalPlaces: number;
  }

  export interface FareConditions {
    category: string;
    situation: string;
    details: Detail[];
  }

  export interface Detail {
    descriptionCode: string;
    defaultDescription: string;
    isAllowed: boolean;
    penalty?: Penalty;
  }

  export interface Penalty {
    code: string;
    description: string;
    price: PenaltyPrice;
  }

  export interface PenaltyPrice {
    total: number;
    currencyCode: Currency['name'];
    totalTaxes: number;
  }

  export interface FareFamilyWithServices {
    hierarchy: number;
    commercialFareFamily: string;
    cabin: string;
  }

  export interface Flight {
    marketingAirlineCode: string;
    operatingAirlineName?: string;
    marketingFlightNumber: string;
    operatingAirlineCode?: string;
    operatingAirlineFlightNumber?: string;
    departure: DepartureArrivalInfo;
    arrival: DepartureArrivalInfo;
    aircraftCode: string;
    duration: number;
    isOpenSegment: boolean;
    secureFlightIndicator: boolean;
  }

  export interface DepartureArrivalInfo {
    locationCode: string;
    dateTime: string;
    terminal?: string;
  }

  export interface Location {
    type: string;
    airportName?: string;
    cityCode: string;
    cityName: string;
    countryCode: string;
    stateCode?: string;
  }

  export interface Meta {
    office: Office;
  }

  export interface Office {
    officeId: string;
  }
}
